import { useState, useRef, useEffect } from 'react'
import { TAGS_EMAIL } from '../utils/emailTags'

// Editor do corpo do e-mail com dois modos: Visual (contentEditable + toolbar basica, salva como
// HTML) e HTML (textarea com o codigo-fonte, pra quem quiser colar/ajustar um modelo pronto). Os
// dois editam o mesmo `value` (sempre uma string HTML) — o backend decide como enviar (ver
// routes/email.js: se nao tiver nenhuma tag, trata como texto puro por compatibilidade com
// modelos antigos).
const BtnToolbar = ({ onClick, title, children, ativo }) => (
  <button type="button" title={title} onMouseDown={e => e.preventDefault()} onClick={onClick}
    style={{
      border: '1px solid var(--border)', background: ativo ? 'rgba(var(--primary-rgb),0.12)' : '#fff',
      borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 13, lineHeight: 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333',
    }}>
    {children}
  </button>
)

export default function EditorCorpoEmail({ value, onChange }) {
  const [modo, setModo] = useState('visual')
  const editorRef = useRef(null)
  const textareaRef = useRef(null)
  const mudancaInterna = useRef(false)

  // So sincroniza o contentEditable quando o value mudou de fora (trocar de modelo, limpar o
  // form...) — se sincronizasse toda vez, o cursor voltaria pro inicio a cada tecla digitada.
  useEffect(() => {
    if (modo === 'visual' && editorRef.current && !mudancaInterna.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || ''
    }
    mudancaInterna.current = false
  }, [value, modo])

  const aplicar = (comando, arg) => {
    editorRef.current?.focus()
    document.execCommand(comando, false, arg)
    mudancaInterna.current = true
    onChange(editorRef.current.innerHTML)
  }

  const inserirLink = () => {
    const url = window.prompt('URL do link:', 'https://')
    if (url) aplicar('createLink', url)
  }

  const inserirTag = (tag) => {
    if (!tag) return
    if (modo === 'visual') {
      editorRef.current?.focus()
      document.execCommand('insertText', false, tag)
      mudancaInterna.current = true
      onChange(editorRef.current.innerHTML)
    } else {
      const ta = textareaRef.current
      const inicio = ta?.selectionStart ?? value.length
      const fim = ta?.selectionEnd ?? value.length
      const novo = value.slice(0, inicio) + tag + value.slice(fim)
      onChange(novo)
      requestAnimationFrame(() => { ta?.focus(); ta?.setSelectionRange(inicio + tag.length, inicio + tag.length) })
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 2, border: '1px solid var(--border)', borderRadius: 7, padding: 2 }}>
          <button type="button" onClick={() => setModo('visual')}
            style={{ border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: modo === 'visual' ? 'var(--primary)' : 'transparent', color: modo === 'visual' ? '#fff' : '#555' }}>
            Visual
          </button>
          <button type="button" onClick={() => setModo('html')}
            style={{ border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: modo === 'html' ? 'var(--primary)' : 'transparent', color: modo === 'html' ? '#fff' : '#555' }}>
            HTML
          </button>
        </div>

        {modo === 'visual' && (
          <div style={{ display: 'flex', gap: 4 }}>
            <BtnToolbar title="Negrito" onClick={() => aplicar('bold')}><b>N</b></BtnToolbar>
            <BtnToolbar title="Itálico" onClick={() => aplicar('italic')}><i>I</i></BtnToolbar>
            <BtnToolbar title="Sublinhado" onClick={() => aplicar('underline')}><u>S</u></BtnToolbar>
            <BtnToolbar title="Link" onClick={inserirLink}>🔗</BtnToolbar>
            <BtnToolbar title="Limpar formatação" onClick={() => aplicar('removeFormat')}>✕</BtnToolbar>
          </div>
        )}

        <select className="form-select" style={{ padding: '4px 8px', fontSize: 12, maxWidth: 180, marginLeft: 'auto' }}
          value="" onChange={e => inserirTag(e.target.value)}>
          <option value="">+ Inserir tag</option>
          {TAGS_EMAIL.map(t => <option key={t.tag} value={t.tag}>{t.tag}</option>)}
        </select>
      </div>

      {modo === 'visual' ? (
        <div
          ref={editorRef}
          className="form-input"
          contentEditable
          suppressContentEditableWarning
          onInput={e => { mudancaInterna.current = true; onChange(e.currentTarget.innerHTML) }}
          style={{ minHeight: 180, maxHeight: 360, overflowY: 'auto', cursor: 'text' }}
        />
      ) : (
        <textarea
          ref={textareaRef}
          className="form-input"
          rows={10}
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
        />
      )}
    </div>
  )
}
